import React, { useState } from 'react';
import { Card, Table, Form, Button, DatePicker, Select, message, Tag, Tooltip, Progress, Modal } from 'antd';
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, ClockCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ClientSelectWrapper from './ClientSelectWrapper';
import HubSelectWrapper from './HubSelectWrapper';
import {
    GET_CLIENT_HUB_ZONE_IMPORT_TASK_LISTING,
    GetClientHubZoneImportTaskListingResponse,
    GetClientHubZoneImportTaskListingVariables,
    ClientHubZoneImportTaskType
} from '../graphql/queries/getClientHubZoneImportTaskListing';
import {
    CREATE_CLIENT_HUB_ZONE_IMPORT_TASK_DOWNLOAD_TASK,
    CreateClientHubZoneImportTaskDownloadTaskResponse,
    CreateClientHubZoneImportTaskDownloadTaskVariables
} from '../graphql/mutations/createClientHubZoneImportTaskDownloadTask';
import { useTranslation } from 'react-i18next';
import ParcelsDownloadModal from '../../../parcels/components/ParcelsDownloadModal';

dayjs.extend(utc);

const { RangePicker } = DatePicker;
const { Option } = Select;

const TASK_STATUS_MAP = {
    0: { text: '待处理', color: 'default', icon: <ClockCircleOutlined /> },
    1: { text: '处理中', color: 'processing', icon: <SyncOutlined spin /> },
    2: { text: '完成', color: 'success', icon: <CheckCircleOutlined /> },
    3: { text: '失败', color: 'error', icon: <CloseCircleOutlined /> }
};

const TaskHistoryTab: React.FC = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [filters, setFilters] = useState<any>({});
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const { data, loading, refetch } = useQuery<
        GetClientHubZoneImportTaskListingResponse,
        GetClientHubZoneImportTaskListingVariables
    >(GET_CLIENT_HUB_ZONE_IMPORT_TASK_LISTING, {
        variables: {
            filters,
            pageRequest: {
                page: pagination.current,
                size: pagination.pageSize,
                sortBy: 'id',
                sortDirection: 'DESC'
            }
        },
        fetchPolicy: 'network-only'
    });

    const [createDownloadTask, { loading: downloading }] = useMutation<
        CreateClientHubZoneImportTaskDownloadTaskResponse,
        CreateClientHubZoneImportTaskDownloadTaskVariables
    >(CREATE_CLIENT_HUB_ZONE_IMPORT_TASK_DOWNLOAD_TASK);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const newFilters: any = {};

        if (values.channelId) newFilters.channelId = values.channelId;
        if (values.hubId) newFilters.hubId = values.hubId;
        if (values.status !== undefined && values.status !== null) newFilters.status = values.status;
        if (values.createdAt && values.createdAt.length === 2) {
            // 用户选择的时间已经是UTC+0时间，直接转换为ISO格式
            newFilters.createdAt = {
                start: dayjs.utc(values.createdAt[0]).format(),
                end: dayjs.utc(values.createdAt[1]).format()
            };
        }

        const newPagination = { ...pagination, current: 1 };
        setFilters(newFilters);
        setPagination(newPagination);
        
        // 立即刷新查询结果，使用新的过滤条件和分页参数
        refetch({
            filters: newFilters,
            pageRequest: {
                page: newPagination.current,
                size: newPagination.pageSize,
                sortBy: 'id',
                sortDirection: 'DESC'
            }
        });
    };

    const handleReset = () => {
        form.resetFields();
        const resetFilters = {};
        const resetPagination = { current: 1, pageSize: 20 };
        setFilters(resetFilters);
        setPagination(resetPagination);
        
        // 立即刷新查询结果，使用重置后的过滤条件和分页参数
        refetch({
            filters: resetFilters,
            pageRequest: {
                page: resetPagination.current,
                size: resetPagination.pageSize,
                sortBy: 'id',
                sortDirection: 'DESC'
            }
        });
    };

    const handleTableChange = (newPagination: any) => {
        setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize
        });
    };

    const handleDownload = (record: ClientHubZoneImportTaskType) => {
        Modal.confirm({
            title: '确认提交下载中心',
            content: `确定要将文件 "${record.filename}" 提交到下载中心吗？`,
            okText: '提交',
            cancelText: '取消',
            onOk: async () => {
                try {
                    const { data } = await createDownloadTask({
                        variables: { taskNo: record.taskNo }
                    });
                    const fileName = data?.createClientHubZoneImportTaskDownloadTask;
                    if (!fileName) {
                        message.error('提交下载中心失败，请稍后重试');
                        return;
                    }
                    setGeneratedFileName(fileName);
                    setDownloadModalVisible(true);
                } catch (error: any) {
                    message.error(error.message || '提交下载中心失败，请稍后重试');
                }
            }
        });
    };

    const columns = [
        {
            title: '任务编号',
            dataIndex: 'taskNo',
            key: 'taskNo',
            width: 180,
            render: (taskNo: string) => <span className="font-mono text-blue-600">{taskNo}</span>
        },
        {
            title: '文件名',
            dataIndex: 'filename',
            key: 'filename',
            width: 200,
            ellipsis: {
                showTitle: false
            },
            render: (filename: string, record: ClientHubZoneImportTaskType) => (
                <Tooltip title="点击下载文件">
                    <span
                        className="text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
                        onClick={() => handleDownload(record)}
                    >
                        <DownloadOutlined className={`mr-1 ${downloading ? 'animate-spin' : ''}`} />
                        {filename}
                    </span>
                </Tooltip>
            )
        },
        {
            title: '客户/Hub',
            key: 'channelHub',
            width: 200,
            render: (record: ClientHubZoneImportTaskType) => (
                <div className="space-y-1">
                    <Tag color="blue">
                        客户: {record.channelName || record.channelId}
                        {record.channelName && <span className="ml-1 text-xs text-gray-500">(ID: {record.channelId})</span>}
                    </Tag>
                    <Tag color="cyan">
                        Hub: {record.hubName || record.hubId}
                        {record.hubName && <span className="ml-1 text-xs text-gray-500">(ID: {record.hubId})</span>}
                    </Tag>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: number) => {
                const statusInfo = TASK_STATUS_MAP[status as keyof typeof TASK_STATUS_MAP];
                return (
                    <Tag icon={statusInfo.icon} color={statusInfo.color}>
                        {statusInfo.text}
                    </Tag>
                );
            }
        },
        {
            title: '进度',
            key: 'progress',
            width: 200,
            render: (record: ClientHubZoneImportTaskType) => {
                const percent = record.totalCount > 0
                    ? Math.round((record.importedCount / record.totalCount) * 100)
                    : 0;
                const status = record.status === 3 ? 'exception' : record.status === 2 ? 'success' : 'active';
                
                return (
                    <div>
                        <Progress
                            percent={percent}
                            size="small"
                            status={status}
                            format={() => `${record.importedCount}/${record.totalCount}`}
                        />
                        {record.failedCount > 0 && (
                            <span className="text-red-500 text-xs">失败: {record.failedCount}</span>
                        )}
                    </div>
                );
            }
        },
        {
            title: '提交人',
            dataIndex: 'submitterId',
            key: 'submitterId',
            width: 100,
            render: (submitterId: number) => <Tag>ID: {submitterId}</Tag>
        },
        {
            title: '生效时间（UTC+0）',
            dataIndex: 'activeTime',
            key: 'activeTime',
            width: 200,
            render: (timestamp: number | null) => timestamp ? dayjs.unix(timestamp).utc().format('YYYY-MM-DD HH:mm:ss') : '-'
        },
        {
            title: '创建时间（UTC+0）',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
            render: (timestamp: number) => dayjs.unix(timestamp).utc().format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: '错误信息',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            width: 200,
            ellipsis: {
                showTitle: false
            },
            render: (errorMessage: string) => errorMessage ? (
                <Tooltip title={errorMessage}>
                    <span className="text-red-500">{errorMessage}</span>
                </Tooltip>
            ) : <span className="text-gray-400">-</span>
        }
    ];

    const listingData = data?.getClientHubZoneImportTaskListing;

    return (
        <div className="p-6">
            <Card className="mb-4">
                <Form form={form} layout="vertical">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Form.Item label="客户" name="channelId">
                            <ClientSelectWrapper placeholder="选择客户" />
                        </Form.Item>
                        <Form.Item label="Hub" name="hubId">
                            <HubSelectWrapper placeholder="选择Hub" />
                        </Form.Item>
                        <Form.Item label="任务状态" name="status">
                            <Select placeholder="选择状态" allowClear>
                                <Option value={0}>待处理</Option>
                                <Option value={1}>处理中</Option>
                                <Option value={2}>完成</Option>
                                <Option value={3}>失败</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item 
                            label={
                                <span>
                                    创建时间范围
                                    <span className="ml-2 text-orange-600 font-semibold text-xs">（UTC+0）</span>
                                </span>
                            }
                            name="createdAt"
                            extra={
                                <span className="text-gray-500 text-xs">
                                    当前 UTC+0 时间：{dayjs.utc().format('YYYY-MM-DD HH:mm:ss')} UTC+0
                                </span>
                            }
                        >
                            <RangePicker
                                showTime
                                className="w-full"
                                format="YYYY-MM-DD HH:mm"
                                placeholder={['开始时间（UTC+0）', '结束时间（UTC+0）']}
                                value={form.getFieldValue('createdAt') ? [
                                    form.getFieldValue('createdAt')[0] ? dayjs.utc(form.getFieldValue('createdAt')[0]) : null,
                                    form.getFieldValue('createdAt')[1] ? dayjs.utc(form.getFieldValue('createdAt')[1]) : null
                                ] : undefined}
                                onChange={(dates) => {
                                    if (dates && dates[0] && dates[1]) {
                                        // 将选择的日期转换为UTC时间
                                        form.setFieldsValue({
                                            createdAt: [
                                                dayjs.utc(dates[0].format('YYYY-MM-DD HH:mm:ss')),
                                                dayjs.utc(dates[1].format('YYYY-MM-DD HH:mm:ss'))
                                            ]
                                        });
                                    } else {
                                        form.setFieldsValue({ createdAt: null });
                                    }
                                }}
                            />
                        </Form.Item>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button onClick={handleReset}>
                            重置
                        </Button>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                        >
                            查询
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => refetch()}
                        >
                            刷新
                        </Button>
                    </div>
                </Form>
            </Card>

            <Card
                title={
                    <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold">
                            任务历史
                            {listingData && (
                                <span className="ml-2 text-sm text-gray-500 font-normal">
                                    共 {listingData.totalElements} 条记录
                                </span>
                            )}
                        </span>
                    </div>
                }
            >
                <Table
                    columns={columns}
                    dataSource={listingData?.content || []}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: listingData?.totalElements || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => t('table.pagination.totalItems', { total }),
                        pageSizeOptions: ['10', '20', '50', '100']
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1600 }}
                    locale={{
                        emptyText: (
                            <div className="py-8">
                                <div className="text-gray-400 text-lg mb-2">暂无任务记录</div>
                                <div className="text-gray-400 text-sm">请先导入配置文件</div>
                            </div>
                        )
                    }}
                />
            </Card>
            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default TaskHistoryTab;

